import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import { Checks, FunnelSimple, Trash, X } from "@phosphor-icons/react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Indicator,
  Kebab,
} from "@hulahoop/ui";
import { IconTooltip } from "@/components/shared/overlays/IconTooltip";
import { ShellCounter } from "./ShellCounter";
import { ShellSidePanelFrame } from "./ShellSidePanelFrame";
import { ShellInboxList } from "./ShellInboxList";
import type { ShellInboxState } from "./useShellInbox";

function ShellInboxFilter({ inbox }: { inbox: ShellInboxState }) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <IconTooltip label={t("notifications.filter")}>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t("notifications.filter_inbox")}
              nativeButton
            >
              {inbox.filter === "all" ? (
                <FunnelSimple />
              ) : (
                <Indicator label={t("notifications.filter_active")}>
                  <FunnelSimple />
                </Indicator>
              )}
            </Button>
          }
        />
      </IconTooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuRadioGroup value={inbox.filter} onValueChange={inbox.handleFilterChange}>
          <DropdownMenuRadioItem value="all">
            {t("notifications.all_notifications")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="unread">
            {t("notifications.unread_only")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="read">{t("notifications.read_only")}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ShellInboxHeader({ inbox, onClose }: { inbox: ShellInboxState; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-(--control-h-lg) shrink-0 items-center gap-1.5 border-b border-border px-2">
      <span className="px-1 text-sm font-medium">{t("notifications.inbox")}</span>
      {inbox.unreadCount > 0 && <ShellCounter n={inbox.unreadCount} />}
      <span className="ml-auto flex items-center gap-0.5">
        <ShellInboxFilter inbox={inbox} />
        <Kebab label={t("notifications.inbox_options")} pinned>
          <DropdownMenuItem
            disabled={inbox.isBusy || inbox.unreadCount === 0}
            onClick={() => void inbox.handleMarkAll()}
          >
            <Checks /> {t("notifications.mark_all_read")}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            disabled={inbox.isBusy || inbox.totalCount === 0}
            onClick={inbox.handleDeleteAll}
          >
            <Trash /> {t("notifications.delete_all")}
          </DropdownMenuItem>
        </Kebab>
        <IconTooltip label={t("shell.close")}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t("notifications.close_inbox")}
            onClick={onClose}
          >
            <X />
          </Button>
        </IconTooltip>
      </span>
    </div>
  );
}

interface ShellInboxProps {
  inbox: ShellInboxState;
  onClose: () => void;
  ref?: Ref<HTMLElement>;
}

export function ShellInbox({ inbox, onClose, ref }: ShellInboxProps) {
  const { t } = useTranslation();
  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-(--z-overlay) overflow-hidden"
      style={{ top: "var(--control-h-xl)", bottom: "calc(var(--spacing) * 3)" }}
    >
      <ShellSidePanelFrame side="left" className="pointer-events-auto">
        <aside
          ref={ref}
          aria-label={t("shell.inbox")}
          className="flex w-xs max-w-full min-w-0 flex-col"
        >
          <ShellInboxHeader inbox={inbox} onClose={onClose} />
          {inbox.failed && (
            <p role="alert" className="px-3 pb-1 text-(length:--font-caption) text-destructive">
              {t("shell.action_failed")}
            </p>
          )}
          <ShellInboxList inbox={inbox} />
        </aside>
        {inbox.dialog}
      </ShellSidePanelFrame>
    </div>
  );
}
